/**
 * ATR TP / SL + position-size SUGGESTIONS on `.VEST` charts (#637). Pure: no
 * store, no chart, no socket.
 *
 * SOURCE. tradovate-service computes the suggestion (`broker:perps-sizing`
 * -> `broker:perps-sizing-result`): ATR on the market's own klines, SL / TP
 * distances = ATR x multiplier rounded to the tick, size from the configured
 * risk $ (fees included). The browser never recomputes ATR; the distances
 * are used as they come.
 *
 * DRAWING. Suggestions only, never an order: faint dotted price lines with no
 * axis tag.
 *  - Flat in the chart's market: both sides around the last price
 *    (`sugg L SL` / `sugg L TP` below / above, `sugg S SL` / `sugg S TP`).
 *  - A #636 position open in that market: on the position's side only,
 *    relative to its ENTRY (`sugg SL`, `sugg TP`), and a suggestion within
 *    half a tick of the real TP / SL is not drawn (the real line already says it).
 *
 * Every non-`.VEST` chart (NQ / MNQ / ES / MES ...) gets nothing.
 */

import { isVestFeedSymbol, roundToTick, tickDecimals, vestFeedMarket } from './vestFeed';
import { vestChartSymbolFor, vestRowMatchesChart, type VestPositionRow, type VestPriceLineSpec } from './vestPositionLines';

export interface VestSizingSpec {
  tickSize: number | null;
  priceDecimals: number | null;
  sizeDecimals: number | null;
  minSizeUnits: number | null;
}

export interface VestSizingSuggestion {
  atr: number;
  slDist: number;
  tpDist: number;
  size: number | null;
  riskUsd: number | null;
  rewardUsd: number | null;
  feeUsd: number | null;
  riskBudgetUsd: number | null;
  capApplied: 'maxSize' | 'leverage' | null;
  noSizeReason: string | null;
  note: string | null;
  /** Reference price (last closed bar close). */
  price: number | null;
  atrLength: number | null;
  timeframe: string | null;
  slMult: number | null;
  tpMult: number | null;
  stale: boolean;
  warnings: string[];
}

/** The last answer for one market: a suggestion, or why there is none. */
export type VestSizingEntry =
  | { ok: true; market: string; displaySymbol: string; spec: VestSizingSpec; suggestion: VestSizingSuggestion; receivedAt: number }
  | { ok: false; market: string; code: string; message: string; receivedAt: number };

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pos(v: unknown): number | null {
  const n = num(v);
  return n !== null && n > 0 ? n : null;
}

function decimals(v: unknown): number | null {
  const n = num(v);
  return n !== null && n >= 0 && n <= 12 ? Math.round(n) : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

/** Upper-case market name of a result's symbol (`btc-perp` -> `BTC-PERP`). */
export function sizingMarketOf(payload: { symbol?: unknown } | null | undefined): string | null {
  const s = payload && typeof payload.symbol === 'string' ? payload.symbol.trim().toUpperCase() : '';
  return s || null;
}

/**
 * One `broker:perps-sizing-result` as an entry, or null when it is malformed
 * (a success without usable distances is malformed, not a suggestion).
 */
export function parseVestSizingResult(payload: unknown, now: number = Date.now()): VestSizingEntry | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const market = sizingMarketOf(p as { symbol?: unknown });
  if (!market) return null;
  if (p.success === false) {
    const code = str(p.code) ?? 'ERROR';
    return { ok: false, market, code, message: str(p.message) ?? code, receivedAt: now };
  }
  if (p.success !== true) return null;
  const sg = p.suggestion && typeof p.suggestion === 'object' ? (p.suggestion as Record<string, unknown>) : null;
  const sp = p.spec && typeof p.spec === 'object' ? (p.spec as Record<string, unknown>) : {};
  if (!sg) return null;
  const atr = pos(sg.atr);
  const slDist = pos(sg.slDist);
  const tpDist = pos(sg.tpDist);
  if (atr === null || slDist === null || tpDist === null) return null;
  const cap = sg.capApplied === 'maxSize' || sg.capApplied === 'leverage' ? sg.capApplied : null;
  return {
    ok: true,
    market,
    displaySymbol: (str(p.displaySymbol) ?? market).toUpperCase(),
    spec: {
      tickSize: pos(sp.tickSize),
      priceDecimals: decimals(sp.priceDecimals),
      sizeDecimals: decimals(sp.sizeDecimals),
      minSizeUnits: pos(sp.minSizeUnits),
    },
    suggestion: {
      atr,
      slDist,
      tpDist,
      size: pos(sg.size),
      riskUsd: num(sg.riskUsd),
      rewardUsd: num(sg.rewardUsd),
      feeUsd: num(sg.feeUsd),
      riskBudgetUsd: pos(sg.riskBudgetUsd),
      capApplied: cap,
      noSizeReason: str(sg.noSizeReason),
      note: str(sg.note),
      price: pos(sg.price),
      atrLength: pos(sg.atrLength),
      timeframe: str(sg.timeframe),
      slMult: pos(sg.slMult),
      tpMult: pos(sg.tpMult),
      stale: sg.stale === true,
      warnings: Array.isArray(sg.warnings) ? sg.warnings.filter((w): w is string => typeof w === 'string' && w !== '') : [],
    },
    receivedAt: now,
  };
}

/** `BTC-PERP` -> `BTC`, `AAPL-USD-PERP` -> `AAPL`. */
export function sizingBaseAsset(market: string): string {
  const i = market.indexOf('-');
  return i > 0 ? market.slice(0, i) : market;
}

/** Price decimals: the result's priceDecimals, else its tick's, else the chart tick's, else 2. */
export function sizingPriceDecimals(spec: VestSizingSpec, fallbackTick: number | null): number {
  if (spec.priceDecimals !== null) return spec.priceDecimals;
  if (spec.tickSize !== null) return tickDecimals(spec.tickSize);
  if (fallbackTick !== null && fallbackTick > 0) return tickDecimals(fallbackTick);
  return 2;
}

function money(n: number): string {
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

/** True when the entry is for this chart's market (and the chart is a non-index `.VEST` chart). */
function entryMatchesChart(entry: VestSizingEntry | null | undefined, chartSymbol: string | null | undefined): entry is VestSizingEntry {
  if (!entry || !isVestFeedSymbol(chartSymbol)) return false;
  const market = vestFeedMarket(chartSymbol);
  if (!market || vestChartSymbolFor(market) === null) return false;
  return entry.market === market;
}

export interface VestSizingLegend {
  /** One line, e.g. `ATR 47.0 · SL 70.5 · TP 94.0 · 0.5820 BTC @ $50`. */
  text: string;
  /** Hover detail (timeframe, multipliers, $ at SL / TP, fees, every warning). */
  detail: string;
  tone: 'normal' | 'warn' | 'error';
}

/**
 * The legend for `chartSymbol`, or null (non-`.VEST` chart, no answer yet,
 * or an answer for another market).
 */
export function formatVestSizingLegend(
  entry: VestSizingEntry | null | undefined,
  chartSymbol: string | null | undefined,
  fallbackTick: number | null = null,
): VestSizingLegend | null {
  if (!entryMatchesChart(entry, chartSymbol)) return null;
  if (!entry.ok) {
    return { text: `ATR sizing: ${entry.message}`, detail: `${entry.code}: ${entry.message}`, tone: 'error' };
  }
  const { spec, suggestion: s } = entry;
  const d = sizingPriceDecimals(spec, fallbackTick);
  const parts = [`ATR ${s.atr.toFixed(d)}`, `SL ${s.slDist.toFixed(d)}`, `TP ${s.tpDist.toFixed(d)}`];
  if (s.size !== null) {
    const sizeText = spec.sizeDecimals !== null ? s.size.toFixed(spec.sizeDecimals) : String(Number(s.size.toFixed(8)));
    parts.push(`${sizeText} ${sizingBaseAsset(entry.market)}${s.riskBudgetUsd !== null ? ` @ ${money(s.riskBudgetUsd)}` : ''}`);
    if (s.capApplied && s.note) parts.push(s.note);
  } else {
    parts.push(s.note ?? (s.noSizeReason === 'NO_RISK' ? 'set risk $ to get a size' : 'no size'));
  }
  if (s.warnings.length > 0) parts.push(`! ${s.warnings[0]}${s.warnings.length > 1 ? ` (+${s.warnings.length - 1})` : ''}`);
  if (s.stale) parts.push('stale');

  const detail: string[] = [];
  const rule = [
    s.timeframe ? `${s.timeframe}` : null,
    s.atrLength !== null ? `ATR(${s.atrLength})` : 'ATR',
    s.slMult !== null ? `SL ${s.slMult}x` : null,
    s.tpMult !== null ? `TP ${s.tpMult}x` : null,
  ].filter(Boolean).join(' ');
  detail.push(rule);
  if (s.riskUsd !== null) detail.push(`loss at SL $${s.riskUsd.toFixed(2)}`);
  if (s.rewardUsd !== null) detail.push(`gain at TP $${s.rewardUsd.toFixed(2)}`);
  if (s.feeUsd !== null) detail.push(`fees $${s.feeUsd.toFixed(2)}`);
  if (s.note) detail.push(s.note);
  for (const w of s.warnings) detail.push(w);
  if (s.stale) detail.push('ATR not refreshed on time (stale)');
  detail.push('suggestion only, no order is placed');

  return {
    text: parts.join(' · '),
    detail: detail.join('\n'),
    tone: s.warnings.length > 0 || s.stale ? 'warn' : 'normal',
  };
}

// --- lines ------------------------------------------------------------------

/** Faint versions of the #636 TP / SL greens and reds. */
export const VEST_SIZING_COLORS = {
  tp: 'rgba(52, 211, 153, 0.45)',
  sl: 'rgba(248, 113, 113, 0.45)',
} as const;

/** lightweight-charts LineStyle.Dotted. */
const LINE_DOTTED = 1;

function line(key: string, kind: 'tp' | 'sl', price: number, title: string): VestPriceLineSpec {
  return {
    key,
    kind,
    price,
    color: kind === 'tp' ? VEST_SIZING_COLORS.tp : VEST_SIZING_COLORS.sl,
    lineWidth: 1,
    lineStyle: LINE_DOTTED,
    title,
    axisLabelVisible: false,
  };
}

/**
 * The suggested lines on `chartSymbol`. `rows` are the #636 position rows
 * (all brokers); `lastPrice` the chart's last trade. Empty for every
 * non-`.VEST` chart, without an answer for this market, or on a failure.
 */
export function deriveVestSizingLines(
  entry: VestSizingEntry | null | undefined,
  chartSymbol: string | null | undefined,
  lastPrice: number | null | undefined,
  rows: readonly VestPositionRow[],
  fallbackTick: number | null = null,
): VestPriceLineSpec[] {
  if (!entryMatchesChart(entry, chartSymbol) || !entry.ok) return [];
  const { slDist, tpDist } = entry.suggestion;
  const tick = entry.spec.tickSize ?? (fallbackTick !== null && fallbackTick > 0 ? fallbackTick : null);
  const snap = (v: number) => (tick !== null ? roundToTick(v, tick) : v);
  const base = `sizing:${entry.market}`;

  const open = rows.filter(r => vestRowMatchesChart(r, chartSymbol));
  if (open.length > 0) {
    const out: VestPriceLineSpec[] = [];
    for (const row of open) {
      const dir = row.side === 'long' ? 1 : -1;
      const rowTick = row.tickSize ?? tick;
      const half = rowTick !== null ? rowTick / 2 : 1e-9;
      const sl = snap(row.entryPrice - dir * slDist);
      const tp = snap(row.entryPrice + dir * tpDist);
      const key = `${base}:${row.brokerId}`;
      if (!(row.stopLossPrice !== null && Math.abs(row.stopLossPrice - sl) <= half) && sl > 0) {
        out.push(line(`${key}:sl`, 'sl', sl, 'sugg SL'));
      }
      if (!(row.takeProfitPrice !== null && Math.abs(row.takeProfitPrice - tp) <= half) && tp > 0) {
        out.push(line(`${key}:tp`, 'tp', tp, 'sugg TP'));
      }
    }
    return out;
  }

  // Flat: around the last price. A last price more than 50% from the
  // suggestion's reference is a previous symbol's: use the reference instead.
  const ref = entry.suggestion.price;
  const last = typeof lastPrice === 'number' && Number.isFinite(lastPrice) && lastPrice > 0 ? lastPrice : null;
  const at = last !== null && (ref === null || Math.abs(last - ref) / ref <= 0.5) ? last : ref;
  if (at === null) return [];
  const out = [
    line(`${base}:flat:long:sl`, 'sl', snap(at - slDist), 'sugg L SL'),
    line(`${base}:flat:long:tp`, 'tp', snap(at + tpDist), 'sugg L TP'),
    line(`${base}:flat:short:sl`, 'sl', snap(at + slDist), 'sugg S SL'),
    line(`${base}:flat:short:tp`, 'tp', snap(at - tpDist), 'sugg S TP'),
  ];
  return out.filter(l => l.price > 0);
}
